import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as XLSX from 'xlsx';
import {
  BarChart3, TrendingUp, Users, Loader2, AlertCircle, Calendar,
  Euro, ShoppingCart, UserCheck, Activity, RefreshCw, Download,
  Search, X, Award, LineChart as LineChartIcon, BarChart2, Plus,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, AreaChart, Area, Cell,
} from 'recharts';

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const YEAR_COLORS = ['#0099FF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
const PALETTE = [
  '#0099FF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899',
  '#06B6D4', '#84CC16', '#F97316', '#6366F1', '#14B8A6', '#D946EF',
];

const formatCurrency = (v) => {
  if (v == null || isNaN(v)) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v);
};

const formatCurrency0 = (v) => {
  if (v == null || isNaN(v)) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
};

const formatCompact = (v) => {
  if (v == null || isNaN(v)) return '—';
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M€`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k€`;
  return `${v.toFixed(0)}€`;
};

const generateYearOptions = () => {
  const current = new Date().getFullYear();
  return Array.from({ length: 8 }, (_, i) => current - i);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const sorted = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-xl p-3 text-sm min-w-[200px]">
      <p className="font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-100">{label}</p>
      {sorted.map((entry, i) => (
        <div key={i} className="flex items-center justify-between gap-4 py-0.5">
          <span className="flex items-center gap-1.5 text-gray-600">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
            {entry.name}
          </span>
          <span className="font-semibold text-gray-800">{formatCurrency(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

const BarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-xl p-3 text-sm">
      <p className="font-semibold text-gray-700 mb-1 max-w-[240px] truncate">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center justify-between gap-4">
          <span className="text-gray-500">{entry.name}</span>
          <span className="font-semibold text-gray-800">{entry.name === 'Pedidos' || entry.name === 'Clientes' ? entry.value : formatCurrency(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

/* ─── KPI Card ─── */
const KpiCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-5 flex items-center gap-4">
    <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15` }}>
      <Icon size={26} style={{ color }} />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{label}</p>
      <p className="text-2xl font-bold text-gray-800 truncate leading-tight">{value}</p>
    </div>
  </div>
);

/* ─── Chart Card ─── */
const ChartCard = ({ title, subtitle, children, className = '', actions }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden ${className}`}>
    <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

/* ─── Chart Type Toggle ─── */
const ChartTypeToggle = ({ value, onChange }) => (
  <div className="inline-flex items-center bg-gray-100 rounded-lg p-0.5">
    <button
      onClick={() => onChange('line')}
      className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${value === 'line' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
    >
      <LineChartIcon size={12} /> Línea
    </button>
    <button
      onClick={() => onChange('bar')}
      className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${value === 'bar' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
    >
      <BarChart2 size={12} /> Barras
    </button>
  </div>
);

/* ─── Year Picker ─── */
const YearChipPicker = ({ selected, onChange }) => {
  const years = generateYearOptions();
  const toggle = (y) => {
    if (selected.includes(y)) {
      if (selected.length > 1) onChange(selected.filter(x => x !== y));
    } else {
      onChange([...selected, y].sort((a, b) => a - b));
    }
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {years.map(y => (
        <button
          key={y}
          onClick={() => toggle(y)}
          className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
            selected.includes(y) ? 'text-white shadow-sm scale-105' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
          style={selected.includes(y) ? { backgroundColor: YEAR_COLORS[selected.indexOf(y) % YEAR_COLORS.length] } : {}}
        >
          {y}
        </button>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════ */
const CommercialStats = () => {
  const { token, API_URL } = useAuth();
  const currentYear = new Date().getFullYear();

  const [selectedYears, setSelectedYears] = useState([currentYear]);
  const [desde, setDesde] = useState(`${currentYear}-01-01`);
  const [hasta, setHasta] = useState(new Date().toISOString().split('T')[0]);
  const [useRange, setUseRange] = useState(false);

  const [kpis, setKpis] = useState(null);
  const [monthlyData, setMonthlyData] = useState({});
  const [topClients, setTopClients] = useState([]);
  const [commercialRanking, setCommercialRanking] = useState([]);
  const [teamRanking, setTeamRanking] = useState([]);
  const [teamMonthly, setTeamMonthly] = useState({ members: [], data: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Chart type toggles
  const [evoChartType, setEvoChartType] = useState('line');
  const [comparisonChartType, setComparisonChartType] = useState('line');
  const [monthlyBreakdownType, setMonthlyBreakdownType] = useState('bar');

  // Client comparison
  const [comparisonClients, setComparisonClients] = useState([]); // [{id, razon_social}]
  const [comparisonData, setComparisonData] = useState({}); // { id: { year: [{mes, total}] } }
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTimer = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const kpiParams = useRange ? `desde=${desde}&hasta=${hasta}` : `year=${selectedYears[0]}`;
      const topParams = kpiParams;
      const monthlyParams = `years=${selectedYears.join(',')}`;

      const [kpiRes, monthlyRes, clientsRes, commercialsRes, teamRes, teamMonthlyRes] = await Promise.all([
        fetch(`${API_URL}/commercial/stats/overview?${kpiParams}`, { headers }),
        fetch(`${API_URL}/commercial/stats/monthly?${monthlyParams}`, { headers }),
        fetch(`${API_URL}/commercial/stats/clients/top?${topParams}&limit=15`, { headers }),
        fetch(`${API_URL}/commercial/stats/commercials/ranking?${topParams}`, { headers }),
        fetch(`${API_URL}/commercial/stats/team/ranking?${topParams}`, { headers }),
        fetch(`${API_URL}/commercial/stats/team/monthly?${monthlyParams}`, { headers }),
      ]);

      if (!kpiRes.ok || !monthlyRes.ok || !clientsRes.ok || !commercialsRes.ok) {
        throw new Error('Error al cargar estadísticas');
      }

      const [kpiData, monthlyDataRaw, clientsData, commercialsData, teamData, teamMonthlyData] = await Promise.all([
        kpiRes.json(), monthlyRes.json(), clientsRes.json(), commercialsRes.json(),
        teamRes.ok ? teamRes.json() : { data: [] },
        teamMonthlyRes.ok ? teamMonthlyRes.json() : { members: [], data: {} },
      ]);

      setKpis(kpiData);
      setMonthlyData(monthlyDataRaw);
      setTopClients(clientsData.data || []);
      setCommercialRanking(commercialsData.data || []);
      setTeamRanking(teamData.data || []);
      setTeamMonthly(teamMonthlyData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, selectedYears, desde, hasta, useRange]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Fetch comparison data when clients or filter changes ──
  const fetchComparison = useCallback(async () => {
    if (comparisonClients.length === 0) {
      setComparisonData({});
      return;
    }
    setComparisonLoading(true);
    try {
      const yearsParam = selectedYears.join(',');
      const results = await Promise.all(
        comparisonClients.map(async c => {
          const res = await fetch(
            `${API_URL}/commercial/stats/client-monthly?cliente=${c.id}&years=${yearsParam}`,
            { headers }
          );
          if (!res.ok) return [c.id, { data: {} }];
          const json = await res.json();
          return [c.id, json];
        })
      );
      const map = {};
      results.forEach(([id, json]) => { map[id] = json.data || {}; });
      setComparisonData(map);
    } catch (e) {
      // silent
    } finally {
      setComparisonLoading(false);
    }
  }, [comparisonClients, selectedYears, API_URL, token]);

  useEffect(() => { fetchComparison(); }, [fetchComparison]);

  // ── Client search (debounced) ──
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/clients/search?q=${encodeURIComponent(searchQuery)}`, { headers });
        if (res.ok) {
          const json = await res.json();
          setSearchResults(json.clients || []);
        }
      } catch (e) {}
    }, 250);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [searchQuery, API_URL, token]);

  const addComparisonClient = (c) => {
    if (comparisonClients.some(x => x.id === c.id)) return;
    setComparisonClients([...comparisonClients, c]);
    setSearchQuery('');
    setSearchResults([]);
    setSearchOpen(false);
  };
  const removeComparisonClient = (id) => {
    setComparisonClients(comparisonClients.filter(c => c.id !== id));
  };

  // ── Build chart data ──
  const monthlyChartData = MONTHS.map((m, i) => {
    const row = { mes: m };
    selectedYears.forEach(y => {
      const yearData = monthlyData[y] || [];
      const entry = yearData.find(d => d.mes === i + 1);
      row[y] = entry ? parseFloat(entry.total) || 0 : 0;
    });
    return row;
  });

  const pedidosChartData = MONTHS.map((m, i) => {
    const row = { mes: m };
    selectedYears.forEach(y => {
      const yearData = monthlyData[y] || [];
      const entry = yearData.find(d => d.mes === i + 1);
      row[y] = entry ? parseInt(entry.pedidos) || 0 : 0;
    });
    return row;
  });

  const clientBarData = topClients.slice(0, 15).map(c => ({
    nombre: (c.razon_social || `Cliente ${c.cliente}`).substring(0, 28),
    total: parseFloat(c.total) || 0,
    pedidos: parseInt(c.num_pedidos) || 0,
  }));

  const commercialBarData = commercialRanking.map(c => ({
    nombre: (c.nombrec || `Comercial ${c.idcomercial}`).substring(0, 28),
    total: parseFloat(c.total) || 0,
    pedidos: parseInt(c.num_pedidos) || 0,
  }));

  const yearTotals = selectedYears.map((y, i) => {
    const yearData = monthlyData[y] || [];
    const total = yearData.reduce((sum, d) => sum + (parseFloat(d.total) || 0), 0);
    return { year: String(y), total, color: YEAR_COLORS[i % YEAR_COLORS.length] };
  });

  // Comparison chart data: rows = months, columns = client labels
  // Sums all selected years per month per client
  const comparisonChartData = MONTHS.map((m, i) => {
    const row = { mes: m };
    comparisonClients.forEach(c => {
      const byYear = comparisonData[c.id] || {};
      let sum = 0;
      Object.values(byYear).forEach(arr => {
        const entry = (arr || []).find(d => d.mes === i + 1);
        if (entry) sum += parseFloat(entry.total) || 0;
      });
      row[c.razon_social || `Cliente ${c.id}`] = sum;
    });
    return row;
  });

  // ── Excel export ──
  const exportExcel = async () => {
    const wb = XLSX.utils.book_new();

    // Resumen
    const resumenRows = [
      ['Estadísticas Comerciales'],
      ['Filtro', useRange ? `${desde} a ${hasta}` : `Años: ${selectedYears.join(', ')}`],
      [],
      ['Métrica', 'Valor'],
      ['Total Facturación', parseFloat(kpis?.total_facturacion) || 0],
      ['Total Pedidos', parseInt(kpis?.total_pedidos) || 0],
      ['Media por Pedido', parseFloat(kpis?.media_pedido) || 0],
      ['Clientes Activos', parseInt(kpis?.clientes_activos) || 0],
      ['Comerciales Activos', parseInt(kpis?.comerciales_activos) || 0],
    ];
    const wsResumen = XLSX.utils.aoa_to_sheet(resumenRows);
    wsResumen['!cols'] = [{ wch: 28 }, { wch: 22 }];
    XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

    // Mensual
    const mensualHeader = ['Año', 'Mes', 'Mes #', 'Facturación', 'Pedidos', 'Clientes'];
    const mensualRows = [mensualHeader];
    selectedYears.forEach(y => {
      const yearData = monthlyData[y] || [];
      MONTHS.forEach((mLabel, i) => {
        const entry = yearData.find(d => d.mes === i + 1);
        mensualRows.push([
          y,
          mLabel,
          i + 1,
          entry ? parseFloat(entry.total) || 0 : 0,
          entry ? parseInt(entry.pedidos) || 0 : 0,
          entry ? parseInt(entry.clientes) || 0 : 0,
        ]);
      });
    });
    const wsMensual = XLSX.utils.aoa_to_sheet(mensualRows);
    wsMensual['!cols'] = [{ wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 16 }, { wch: 12 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, wsMensual, 'Mensual');

    // Clientes — fetch ALL (not just top 15)
    let allClients = topClients;
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const params = new URLSearchParams();
      if (!useRange) {
        const lastYear = selectedYears[selectedYears.length - 1];
        params.set('year', lastYear);
      } else {
        if (desde) params.set('desde', desde);
        if (hasta) params.set('hasta', hasta);
      }
      params.set('limit', 100000);
      const res = await fetch(`${API_URL}/commercial/stats/clients/top?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        allClients = data.data || data.clients || allClients;
      }
    } catch (e) { /* fallback to topClients */ }

    const clientesRows = [['#', 'Cliente', 'ID', 'Facturación', 'Pedidos']];
    allClients.forEach((c, i) => clientesRows.push([
      i + 1,
      c.razon_social || `Cliente ${c.cliente}`,
      c.cliente,
      parseFloat(c.total) || 0,
      parseInt(c.num_pedidos) || 0,
    ]));
    const wsClientes = XLSX.utils.aoa_to_sheet(clientesRows);
    wsClientes['!cols'] = [{ wch: 5 }, { wch: 40 }, { wch: 10 }, { wch: 16 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, wsClientes, 'Clientes');

    // Ranking Comerciales
    const comRows = [['#', 'Comercial', 'Facturación', 'Pedidos']];
    commercialRanking.forEach((c, i) => comRows.push([
      i + 1,
      c.nombrec || `Comercial ${c.idcomercial}`,
      parseFloat(c.total) || 0,
      parseInt(c.num_pedidos) || 0,
    ]));
    const wsCom = XLSX.utils.aoa_to_sheet(comRows);
    wsCom['!cols'] = [{ wch: 5 }, { wch: 32 }, { wch: 16 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, wsCom, 'Ranking Comerciales');

    // Comparativa Clientes
    if (comparisonClients.length > 0) {
      const compHeader = ['Mes', ...comparisonClients.map(c => c.razon_social || `Cliente ${c.id}`)];
      const compRows = [compHeader];
      comparisonChartData.forEach(row => {
        compRows.push([row.mes, ...comparisonClients.map(c => row[c.razon_social || `Cliente ${c.id}`] || 0)]);
      });
      const wsComp = XLSX.utils.aoa_to_sheet(compRows);
      wsComp['!cols'] = [{ wch: 8 }, ...comparisonClients.map(() => ({ wch: 18 }))];
      XLSX.utils.book_append_sheet(wb, wsComp, 'Comparativa Clientes');
    }

    const today = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `estadisticas_comerciales_${today}.xlsx`);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gray-50">
      {/* Header + Filters */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-white space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#0099FF] to-blue-600 flex items-center justify-center shadow-md">
              <BarChart3 size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Estadísticas Comerciales</h1>
              <p className="text-xs text-gray-400">Análisis de ventas, clientes y comerciales</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportExcel}
              disabled={loading}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-sm disabled:opacity-50 font-medium"
            >
              <Download size={15} />
              Exportar Excel
            </button>
            <button
              onClick={fetchAll}
              disabled={loading}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 font-medium"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Actualizar
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setUseRange(false)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!useRange ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}
            >Por Año</button>
            <button
              onClick={() => setUseRange(true)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${useRange ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}
            >Rango Fechas</button>
          </div>

          {useRange ? (
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-gray-400" />
              <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <span className="text-gray-400 text-sm">—</span>
              <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 font-medium">Comparar años:</span>
              <YearChipPicker selected={selectedYears} onChange={setSelectedYears} />
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="flex-shrink-0 mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl flex items-center gap-2 text-sm">
          <AlertCircle size={18} /> <span>{error}</span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Loader2 size={36} className="animate-spin text-blue-500" />
            <span className="text-gray-500 text-sm">Cargando estadísticas...</span>
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <KpiCard icon={Euro} label="Facturación" value={formatCurrency0(kpis?.total_facturacion)} color="#0099FF" />
              <KpiCard icon={ShoppingCart} label="Pedidos" value={kpis?.total_pedidos?.toLocaleString('es-ES') ?? '—'} color="#10B981" />
              <KpiCard icon={Activity} label="Media / Pedido" value={formatCurrency0(kpis?.media_pedido)} color="#F59E0B" />
              <KpiCard icon={UserCheck} label="Clientes Activos" value={kpis?.clientes_activos?.toLocaleString('es-ES') ?? '—'} color="#8B5CF6" />
              <KpiCard icon={Users} label="Comerciales Activos" value={kpis?.comerciales_activos?.toLocaleString('es-ES') ?? '—'} color="#EC4899" />
            </div>

            {/* Monthly Evolution */}
            <ChartCard
              title="Evolución Mensual — Facturación"
              subtitle={selectedYears.length > 1 ? `Comparativa ${selectedYears.join(' vs ')}` : `Año ${selectedYears[0]}`}
              actions={<ChartTypeToggle value={evoChartType} onChange={setEvoChartType} />}
            >
              <ResponsiveContainer width="100%" height={340}>
                {evoChartType === 'line' ? (
                  <AreaChart data={monthlyChartData}>
                    <defs>
                      {selectedYears.map((y, i) => (
                        <linearGradient key={y} id={`grad-${y}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={YEAR_COLORS[i % YEAR_COLORS.length]} stopOpacity={0.35} />
                          <stop offset="95%" stopColor={YEAR_COLORS[i % YEAR_COLORS.length]} stopOpacity={0.02} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={formatCompact} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {selectedYears.map((y, i) => (
                      <Area key={y} type="monotone" dataKey={y} name={String(y)}
                        stroke={YEAR_COLORS[i % YEAR_COLORS.length]} strokeWidth={2.5}
                        fill={`url(#grad-${y})`}
                        dot={{ r: 3, fill: YEAR_COLORS[i % YEAR_COLORS.length] }} activeDot={{ r: 5 }} />
                    ))}
                  </AreaChart>
                ) : (
                  <BarChart data={monthlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={formatCompact} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {selectedYears.map((y, i) => (
                      <Bar key={y} dataKey={y} name={String(y)} fill={YEAR_COLORS[i % YEAR_COLORS.length]} radius={[4, 4, 0, 0]} />
                    ))}
                  </BarChart>
                )}
              </ResponsiveContainer>
            </ChartCard>

            {/* ═══ COMPARATIVA DE CLIENTES ═══ */}
            <div className="flex items-center gap-3 pt-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-sm">
                <TrendingUp size={18} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-800">Comparativa de Clientes</h2>
                <p className="text-xs text-gray-400">Compare la facturación mensual de varios clientes</p>
              </div>
            </div>

            <ChartCard
              title="Facturación Mensual por Cliente"
              subtitle={comparisonClients.length === 0 ? 'Añada clientes para comparar' : `${comparisonClients.length} cliente(s) seleccionado(s)`}
              actions={comparisonClients.length > 0 ? <ChartTypeToggle value={comparisonChartType} onChange={setComparisonChartType} /> : null}
            >
              {/* Search bar */}
              <div className="mb-4 relative">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {comparisonClients.map((c, i) => (
                    <span
                      key={c.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white shadow-sm"
                      style={{ backgroundColor: PALETTE[i % PALETTE.length] }}
                    >
                      {c.razon_social || `Cliente ${c.id}`}
                      <button
                        onClick={() => removeComparisonClient(c.id)}
                        className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                    onFocus={() => setSearchOpen(true)}
                    onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                    placeholder="Buscar cliente por nombre..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchOpen && searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto z-20">
                      {searchResults.map(c => (
                        <button
                          key={c.id}
                          onClick={() => addComparisonClient(c)}
                          disabled={comparisonClients.some(x => x.id === c.id)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <Plus size={14} className="text-blue-500" />
                          <span className="truncate">{c.razon_social}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {comparisonClients.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-2 border-2 border-dashed border-gray-200 rounded-xl">
                  <Users size={42} className="text-gray-300" />
                  <p className="text-sm font-medium">Sin clientes para comparar</p>
                  <p className="text-xs">Busque y añada clientes desde el campo superior</p>
                </div>
              ) : comparisonLoading ? (
                <div className="flex items-center justify-center h-96">
                  <Loader2 size={28} className="animate-spin text-blue-500" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={420}>
                  {comparisonChartType === 'line' ? (
                    <LineChart data={comparisonChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={formatCompact} tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      {comparisonClients.map((c, i) => (
                        <Line
                          key={c.id}
                          type="monotone"
                          dataKey={c.razon_social || `Cliente ${c.id}`}
                          stroke={PALETTE[i % PALETTE.length]}
                          strokeWidth={2.5}
                          dot={{ r: 3, fill: PALETTE[i % PALETTE.length] }}
                          activeDot={{ r: 6 }}
                        />
                      ))}
                    </LineChart>
                  ) : (
                    <BarChart data={comparisonChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={formatCompact} tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      {comparisonClients.map((c, i) => (
                        <Bar
                          key={c.id}
                          dataKey={c.razon_social || `Cliente ${c.id}`}
                          fill={PALETTE[i % PALETTE.length]}
                          radius={[4, 4, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  )}
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* Pedidos evolution + Year totals */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <ChartCard
                title="Evolución Mensual — Pedidos"
                subtitle={selectedYears.length > 1 ? `Comparativa ${selectedYears.join(' vs ')}` : `Año ${selectedYears[0]}`}
              >
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={pedidosChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    {selectedYears.map((y, i) => (
                      <Line key={y} type="monotone" dataKey={y} name={String(y)}
                        stroke={YEAR_COLORS[i % YEAR_COLORS.length]} strokeWidth={2.5}
                        dot={{ r: 3, fill: YEAR_COLORS[i % YEAR_COLORS.length] }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>

              {selectedYears.length > 1 && (
                <ChartCard title="Comparativa Anual — Total Facturación" subtitle="Suma anual">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={yearTotals}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={formatCompact} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                      <Bar dataKey="total" name="Total" radius={[8, 8, 0, 0]}>
                        {yearTotals.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              )}
            </div>

            {/* Top Clients + Commercial Ranking */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <ChartCard title={`Top ${Math.min(clientBarData.length, 15)} Clientes`} subtitle="Ordenados por facturación">
                {clientBarData.length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-12">Sin datos</p>
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(320, clientBarData.length * 34)}>
                    <BarChart data={clientBarData} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tickFormatter={formatCompact} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="nombre" width={160} tick={{ fontSize: 11 }} />
                      <Tooltip content={<BarTooltip />} />
                      <Bar dataKey="total" name="Facturación" fill="#0099FF" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="Ranking de Comerciales" subtitle="Ordenados por facturación">
                {commercialBarData.length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-12">Sin datos</p>
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(320, commercialBarData.length * 42)}>
                    <BarChart data={commercialBarData} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tickFormatter={formatCompact} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="nombre" width={160} tick={{ fontSize: 11 }} />
                      <Tooltip content={<BarTooltip />} />
                      <Bar dataKey="total" name="Facturación" fill="#10B981" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            {/* ═══ RENDIMIENTO DEL EQUIPO ═══ */}
            {teamRanking.length > 0 && (
              <>
                <div className="flex items-center gap-3 pt-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                    <Award size={18} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">Rendimiento del Equipo</h2>
                    <p className="text-xs text-gray-400">Quién factura más, quién gestiona más pedidos</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {teamRanking.slice(0, 4).map((member, i) => {
                    const colors = ['#8B5CF6', '#EC4899', '#F59E0B', '#06B6D4'];
                    return (
                      <div key={member.idusuario} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: colors[i % colors.length] }}>
                            {(member.nombrec || member.username || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-800 text-sm truncate">{member.nombrec || member.username}</p>
                            <p className="text-xs text-gray-400">#{i + 1} por facturación</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-center">
                          <div className="bg-gray-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500">Facturación</p>
                            <p className="text-sm font-bold text-gray-800">{formatCompact(parseFloat(member.total_facturacion))}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500">Pedidos</p>
                            <p className="text-sm font-bold text-gray-800">{parseInt(member.total_pedidos).toLocaleString('es-ES')}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <ChartCard title="Facturación por Miembro" subtitle="Ordenado por volumen">
                    <ResponsiveContainer width="100%" height={Math.max(300, teamRanking.length * 42)}>
                      <BarChart
                        data={teamRanking.map(m => ({
                          nombre: (m.nombrec || m.username || `User ${m.idusuario}`).substring(0, 22),
                          facturacion: parseFloat(m.total_facturacion) || 0,
                        }))}
                        layout="vertical" margin={{ left: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tickFormatter={formatCompact} tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="nombre" width={140} tick={{ fontSize: 11 }} />
                        <Tooltip content={<BarTooltip />} />
                        <Bar dataKey="facturacion" name="Facturación" fill="#8B5CF6" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  <ChartCard title="Pedidos por Miembro" subtitle="Cantidad de pedidos y clientes">
                    <ResponsiveContainer width="100%" height={Math.max(300, teamRanking.length * 42)}>
                      <BarChart
                        data={[...teamRanking]
                          .sort((a, b) => parseInt(b.total_pedidos) - parseInt(a.total_pedidos))
                          .map(m => ({
                            nombre: (m.nombrec || m.username || `User ${m.idusuario}`).substring(0, 22),
                            pedidos: parseInt(m.total_pedidos) || 0,
                            clientes: parseInt(m.clientes_atendidos) || 0,
                          }))}
                        layout="vertical" margin={{ left: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="nombre" width={140} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="pedidos" name="Pedidos" fill="#EC4899" radius={[0, 6, 6, 0]} />
                        <Bar dataKey="clientes" name="Clientes" fill="#06B6D4" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </div>

                {teamMonthly.members.length > 0 && (() => {
                  const TEAM_COLORS = ['#8B5CF6', '#EC4899', '#F59E0B', '#06B6D4', '#10B981', '#EF4444', '#0099FF', '#84CC16'];
                  const primaryYear = selectedYears[0];
                  const yearData = teamMonthly.data[primaryYear] || [];
                  const teamMonthlyChart = MONTHS.map((m, i) => {
                    const row = { mes: m };
                    teamMonthly.members.forEach(member => {
                      const entry = yearData.find(d => d.idusuario === member.idusuario && d.mes === i + 1);
                      row[member.nombrec || member.username] = entry ? parseFloat(entry.total) || 0 : 0;
                    });
                    return row;
                  });
                  return (
                    <ChartCard title="Evolución Mensual del Equipo" subtitle={`Facturación por miembro — ${primaryYear}`}>
                      <ResponsiveContainer width="100%" height={380}>
                        <LineChart data={teamMonthlyChart}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                          <YAxis tickFormatter={formatCompact} tick={{ fontSize: 11 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          {teamMonthly.members.map((member, i) => (
                            <Line key={member.idusuario} type="monotone"
                              dataKey={member.nombrec || member.username}
                              stroke={TEAM_COLORS[i % TEAM_COLORS.length]} strokeWidth={2.5}
                              dot={{ r: 3, fill: TEAM_COLORS[i % TEAM_COLORS.length] }} activeDot={{ r: 6 }} />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  );
                })()}

                <ChartCard title="Media por Pedido — Equipo" subtitle="Ticket medio de cada miembro">
                  <ResponsiveContainer width="100%" height={Math.max(300, teamRanking.length * 42)}>
                    <BarChart
                      data={[...teamRanking]
                        .sort((a, b) => parseFloat(b.media_pedido) - parseFloat(a.media_pedido))
                        .map(m => ({
                          nombre: (m.nombrec || m.username || `User ${m.idusuario}`).substring(0, 22),
                          media: parseFloat(m.media_pedido) || 0,
                        }))}
                      layout="vertical" margin={{ left: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tickFormatter={formatCompact} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="nombre" width={140} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                      <Bar dataKey="media" name="Media / Pedido" fill="#F59E0B" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CommercialStats;
